import { COLORS } from '@/config/colors';
import { css, fillOutlined, shade } from './draw-utils';

/*
 * LOOT drawing kit (docs/04 "Tehlike ↔ ganimet dili"): faceted cuts with
 * light facets, a crisp white 4-point sparkle, a soft cool glow — never a
 * dark core, spikes or a red/orange halo (those mean DANGER). Shared by the
 * single pickups (pickups.ts) and the big / ×2 variants (pickups-big.ts).
 */

type Ctx = CanvasRenderingContext2D;

/** Sparkle spots per frame (normalized to the cut radius) — one per frame. */
export const SPARK: ReadonlyArray<readonly [number, number]> = [
  [-0.3, -0.4], [0.35, -0.15], [0, 0.3], [-0.1, -0.1],
];
/** Frame count of every animated loot texture (the sparkle wanders) */
export const LOOT_FRAMES = SPARK.length;
/** Repair medi-gem body colour */
export const REPAIR_GREEN = 0x3f9a4c;

/** Soft cool shine behind every pickup (reads "loot", not "heat"). */
export function lootGlow(ctx: Ctx, cx: number, cy: number, r: number): void {
  const g = ctx.createRadialGradient(cx, cy, r * 0.3, cx, cy, r);
  g.addColorStop(0, 'rgba(255,255,255,0.22)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = g;
  ctx.fill();
}

/** Thin bright ring around a big haul — round and smooth, never spiked. */
export function lootHalo(ctx: Ctx, cx: number, cy: number, r: number, color: number): void {
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.strokeStyle = css(shade(color, 1.3));
  ctx.globalAlpha = 0.55;
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.globalAlpha = 1;
}

/** The loot-only sparkle: a crisp white 4-point star. */
export function sparkle(ctx: Ctx, sx: number, sy: number, s: number): void {
  ctx.beginPath();
  ctx.moveTo(sx, sy - s);
  ctx.lineTo(sx + s * 0.3, sy - s * 0.3);
  ctx.lineTo(sx + s, sy);
  ctx.lineTo(sx + s * 0.3, sy + s * 0.3);
  ctx.lineTo(sx, sy + s);
  ctx.lineTo(sx - s * 0.3, sy + s * 0.3);
  ctx.lineTo(sx - s, sy);
  ctx.lineTo(sx - s * 0.3, sy - s * 0.3);
  ctx.closePath();
  ctx.fillStyle = css(COLORS.lootSparkle);
  ctx.fill();
}

/** Brilliant cut (GEM): crown facets light, a bright table, pavilion lines. */
export function brilliantCut(ctx: Ctx, cx: number, cy: number, r: number, color: number, line = 4): void {
  ctx.beginPath();
  ctx.moveTo(cx, cy - r);
  ctx.lineTo(cx + r * 0.75, cy - r * 0.2);
  ctx.lineTo(cx, cy + r);
  ctx.lineTo(cx - r * 0.75, cy - r * 0.2);
  ctx.closePath();
  fillOutlined(ctx, color, line);
  ctx.beginPath();
  ctx.moveTo(cx, cy - r);
  ctx.lineTo(cx + r * 0.75, cy - r * 0.2);
  ctx.lineTo(cx - r * 0.75, cy - r * 0.2);
  ctx.closePath();
  ctx.fillStyle = css(shade(color, 1.25));
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(cx, cy - r * 0.62);
  ctx.lineTo(cx + r * 0.3, cy - r * 0.2);
  ctx.lineTo(cx - r * 0.3, cy - r * 0.2);
  ctx.closePath();
  ctx.fillStyle = css(shade(color, 1.45));
  ctx.fill();
  ctx.strokeStyle = css(shade(color, 0.72));
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(cx - r * 0.3, cy - r * 0.2);
  ctx.lineTo(cx, cy + r);
  ctx.lineTo(cx + r * 0.3, cy - r * 0.2);
  ctx.stroke();
}

/** Hex cut (CORE): a wider six-sided brilliant with a bright table. */
export function hexCut(ctx: Ctx, cx: number, cy: number, r: number, color: number, line = 4): void {
  const ring = (rr: number): void => {
    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI / 3) * i - Math.PI / 2;
      if (i === 0) ctx.moveTo(cx + Math.cos(a) * rr, cy + Math.sin(a) * rr);
      else ctx.lineTo(cx + Math.cos(a) * rr, cy + Math.sin(a) * rr);
    }
    ctx.closePath();
  };
  ring(r);
  fillOutlined(ctx, color, line);
  ctx.beginPath();
  ctx.moveTo(cx + Math.cos(-Math.PI * 5 / 6) * r, cy + Math.sin(-Math.PI * 5 / 6) * r);
  ctx.lineTo(cx, cy - r);
  ctx.lineTo(cx + Math.cos(-Math.PI / 6) * r, cy + Math.sin(-Math.PI / 6) * r);
  ctx.lineTo(cx, cy);
  ctx.closePath();
  ctx.fillStyle = css(shade(color, 1.25));
  ctx.fill();
  ring(r * 0.5);
  ctx.fillStyle = css(shade(color, 1.45));
  ctx.fill();
  ctx.strokeStyle = css(shade(color, 0.72));
  ctx.lineWidth = 1.5;
  ctx.stroke();
}

/** Faceted heater-shield plate (armour), half-size `s`. */
export function heaterPlate(ctx: Ctx, c: number, cy: number, s: number, plate: number): void {
  ctx.beginPath();
  ctx.moveTo(c, cy - s);
  ctx.lineTo(c + s, cy - s * 0.6);
  ctx.lineTo(c + s * 0.85, cy + s * 0.25);
  ctx.lineTo(c, cy + s * 1.05);
  ctx.lineTo(c - s * 0.85, cy + s * 0.25);
  ctx.lineTo(c - s, cy - s * 0.6);
  ctx.closePath();
  fillOutlined(ctx, plate, 4);
  // Left bevel lit, centre ridge
  ctx.beginPath();
  ctx.moveTo(c, cy - s);
  ctx.lineTo(c - s, cy - s * 0.6);
  ctx.lineTo(c - s * 0.85, cy + s * 0.25);
  ctx.lineTo(c, cy + s * 1.05);
  ctx.closePath();
  ctx.fillStyle = css(shade(plate, 1.25));
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(c, cy - s * 0.7);
  ctx.lineTo(c, cy + s * 0.75);
  ctx.strokeStyle = css(shade(plate, 0.72));
  ctx.lineWidth = 2;
  ctx.stroke();
}

/** Octagon medi-gem with a white cross (repair), radius `r`. */
export function mediGem(ctx: Ctx, cx: number, cy: number, r: number, body: number): void {
  ctx.beginPath();
  for (let i = 0; i < 8; i++) {
    const a = (Math.PI / 4) * i + Math.PI / 8;
    if (i === 0) ctx.moveTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
    else ctx.lineTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
  }
  ctx.closePath();
  fillOutlined(ctx, body, 4.5);
  ctx.beginPath();
  ctx.rect(cx - r * 0.9, cy - r * 0.9, r * 1.8, r * 0.9);
  ctx.fillStyle = 'rgba(255,255,255,0.14)';
  ctx.fill();
  const arm = r * 0.41;
  const len = r * 1.3;
  ctx.fillStyle = css(0xf2fff0);
  ctx.beginPath();
  ctx.roundRect(cx - arm / 2, cy - len / 2, arm, len, 2.5);
  ctx.fill();
  ctx.beginPath();
  ctx.roundRect(cx - len / 2, cy - arm / 2, len, arm, 2.5);
  ctx.fill();
}
